import React, { useEffect, useState } from 'react';
import { CalendarDays, ChevronRight, Loader2, Plus, Users, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { MeetingEditor } from './MeetingEditor';
import { leadMeetingsAPI } from '../lib/api';

const norm = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
export const leadKeyOf = (p) => {
  if (p.source !== 'sheet') return `db:${p.id}`;
  if (p.mail) return `sheet:email:${norm(p.mail)}`;
  if (p.telefono) return `sheet:phone:${(p.telefono || '').replace(/\D/g, '')}`;
  return `sheet:name:${norm(p.contacto)}`;
};
const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Sin fecha');

const PendingSteps = ({ meetings }) => {
  const steps = meetings.flatMap((m) => (m.proximos_pasos || []).filter((s) => !s.hecho))
    .sort((a, b) => (a.fecha || '9999').localeCompare(b.fecha || '9999'));
  if (!steps.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="lead-pending-steps">
      <p className="text-sm font-semibold flex items-center gap-1.5 text-amber-900"><ListChecks className="w-4 h-4" />Próximos pasos pendientes</p>
      {steps.map((s) => (
        <div key={s.id} className="flex justify-between text-sm text-amber-950">
          <span>{s.texto}</span>
          <span className={`text-xs ${s.fecha && s.fecha < today ? 'text-red-600 font-semibold' : 'text-amber-800'}`}>{s.fecha ? fmt(s.fecha) : ''}</span>
        </div>
      ))}
    </div>
  );
};

const MeetingList = ({ meetings, ceos, onOpen }) => {
  const name = (id) => ceos.find((c) => c.id === id)?.name || '';
  if (!meetings.length) return <p className="text-sm text-muted-foreground py-8 text-center" data-testid="lead-meetings-empty">Todavía no hay reuniones con este lead.</p>;
  return (
    <div className="divide-y rounded-lg border" data-testid="lead-meetings-list">
      {meetings.map((m) => (
        <button key={m.id} type="button" onClick={() => onOpen(m)} data-testid={`lead-meeting-item-${m.id}`}
          className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors">
          <CalendarDays className="w-4 h-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{fmt(m.fecha)}</p>
            <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
              <Users className="w-3 h-3" />{[m.ceo_ids.map(name).filter(Boolean).join(', '), m.asistentes_lead].filter(Boolean).join(' · ') || 'Sin asistentes'}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>
      ))}
    </div>
  );
};

export const LeadMeetingsDialog = ({ prospect, onClose }) => {
  const [meetings, setMeetings] = useState(null);
  const [ceos, setCeos] = useState([]);
  const [current, setCurrent] = useState(null);
  const [creating, setCreating] = useState(false);
  const leadKey = prospect ? leadKeyOf(prospect) : null;

  useEffect(() => {
    if (!leadKey) return;
    setMeetings(null); setCurrent(null);
    Promise.all([leadMeetingsAPI.list(leadKey), leadMeetingsAPI.getCeos()])
      .then(([ms, cs]) => { setMeetings(ms); setCeos(cs); })
      .catch((e) => { toast.error(e.message); setMeetings([]); });
  }, [leadKey]);

  const create = async () => {
    setCreating(true);
    try {
      const m = await leadMeetingsAPI.create({ lead_key: leadKey, lead_name: prospect.contacto || '', negocio: prospect.nicho || '', fecha: new Date().toISOString().slice(0, 10) });
      setMeetings((prev) => [m, ...prev]);
      setCurrent(m);
    } catch (e) { toast.error(e.message); } finally { setCreating(false); }
  };
  const onSaved = (saved) => setMeetings((prev) => prev.map((x) => (x.id === saved.id ? saved : x)).sort((a, b) => b.fecha.localeCompare(a.fecha)));

  return (
    <Dialog open={!!prospect} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="lead-meetings-dialog">
        <DialogHeader>
          <DialogTitle>{prospect?.contacto} · Reuniones</DialogTitle>
          <DialogDescription>Notas comerciales. Solo visibles para las CEOs.</DialogDescription>
        </DialogHeader>
        {meetings === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : current ? (
          <MeetingEditor key={current.id} meeting={current} ceos={ceos} onBack={() => setCurrent(null)} onSaved={onSaved}
            onDeleted={(id) => { setMeetings((prev) => prev.filter((x) => x.id !== id)); setCurrent(null); }} />
        ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={create} disabled={creating} data-testid="lead-meeting-new-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Nueva reunión
              </Button>
            </div>
            <PendingSteps meetings={meetings} />
            <MeetingList meetings={meetings} ceos={ceos} onOpen={setCurrent} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
